import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PortfolioSection from '../components/PortfolioSection';
import { Box, Button, Card, Chip, Container, Grid, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SharePopover from '../components/SharePopover';
import API from "../config/axios";
import { api_paths } from '../config/api.paths';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { blue, lightBlue } from '@mui/material/colors';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const theme = useTheme();
  const IMG_PATH = process.env.REACT_APP_IMAGE_BASEURL || "";
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try{
        const response = await API.get(api_paths.PROJECT.get + `/${id}`);
        // console.log("Project Details:", response.data);

        setProject(response?.data?.payload || null);
        setLoading(false);
      }catch(err){
        console.error("Error fetching project details:", err);
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [id]);


  return (
    <Box sx={{ py: 6, mt: 4 }}>
      {
        loading ? (
          <Container maxWidth="lg">
            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
                Back to Portfolio
              </Button>

              <SharePopover url={typeof window !== 'undefined' ? window.location.href : ''} title={project?.title} />
            </Box>

            {/* HERO IMAGE */}
            <Skeleton variant="rounded" height={600} />

            {/* TITLE */}
            <Box mt={5}>
              <Skeleton variant="text" width="10%" height={40} />
              <Skeleton variant="text" width="40%" />
            </Box>

            {/* CLIENT + DURATION */}
            <Grid container spacing={3} mt={5}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Skeleton variant="rounded" height={100} />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Skeleton variant="rounded" height={100} />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Skeleton variant="rounded" height={100} />
              </Grid>
            </Grid>

            {/* ABOUT */}
            <Box mt={6}>
              <Skeleton variant="text" width="20%" height={30} />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="80%" />
            </Box>

            {/* TECHNOLOGIES */}
            <Box mt={5}>
              <Skeleton variant="text" width="20%" height={30} />
              <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
                {[1,2,3,4,5].map((i) => (
                  <Skeleton key={i} variant="rounded" width={80} height={30} />
                ))}
              </Stack>
            </Box>

          </Container>
        ) : (
            project ? (
              <Container maxWidth="lg">

                {/* HEADER */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
                    Back to Portfolio
                  </Button>

                  <SharePopover url={typeof window !== 'undefined' ? window.location.href : ''} title={project?.title} />
                </Box>

                {/* HERO IMAGE */}
                <Paper elevation={0} sx={{ borderRadius: 4 }}>
                  <Box
                    component="img"
                    src={project?.image ? IMG_PATH + project.image : "/placeholder.png"}
                    alt={project?.title}
                    sx={{
                      width: "100%",
                      maxHeight: 600,
                      objectFit: "cover",
                      borderRadius: 3,
                    }}
                  />
                </Paper>

                <Box mt={5}>
                  <Chip 
                    label={project?.category?.name} 
                    size="small" 
                    sx={{
                      backgroundColor: lightBlue[50],
                      color: theme.palette.primary.main,
                      borderRadius: 2,
                      fontWeight: 'normal'
                    }}
                  />
                  <Typography variant="h4" fontWeight="bold" mt={1} color={theme.palette.text.primary}>
                    {project?.title}
                  </Typography>
                </Box>

                {/* CLIENT + DURATION */}
                <Grid container spacing={3} mt={5}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Paper sx={{ p: 2, borderRadius: 3 }} variant='outlined'>
                      <Stack direction="column" alignItems="start">
                        <PersonOutlineOutlinedIcon fontSize="small" />
                        <Typography color="text.secondary" fontSize="small">Client</Typography>
                      </Stack>
                      <Typography variant="p" fontWeight="bold">
                        {project?.client}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <Paper sx={{ p: 2, borderRadius: 3 }} variant='outlined'>
                        <Stack direction="column" alignItems="start">
                          <AccessTimeOutlinedIcon fontSize="small" />
                          <Typography color="text.secondary" fontSize="small">Duration</Typography>
                        </Stack>
                        <Typography variant="p" fontWeight="bold">
                          {project?.duration}
                        </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Paper sx={{ p: 2, borderRadius: 3 }} variant='outlined'>
                      <Stack direction="column" alignItems="start">
                        <PlaceOutlinedIcon fontSize="small" />
                        <Typography color="text.secondary" fontSize="small">Country</Typography>
                      </Stack>
                      <Typography variant="p" fontWeight="bold">
                        UK
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* ABOUT */}
                <Box mt={6}>
                  <Typography variant="h5" fontWeight="bold" mb={2}>
                    About the Project
                  </Typography>
                  <Typography color="text.secondary" lineHeight={1.8}>
                    {project?.description}
                  </Typography>
                </Box>

                {/* TECHNOLOGIES */}
                <Box mt={5}>
                  <Typography variant="h5" fontWeight="bold" mb={2}>
                    Technologies Used
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {project?.technology.map((tech, index) => (
                      <Chip 
                        key={`${tech}-${index}`} 
                        label={tech} 
                        sx={{
                          backgroundColor: theme.palette.custom.tagBg,
                          color: theme.palette.custom.tagText,
                          fontWeight: 300,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>

                {/* SCREENSHOTS */}
                {/* <Box mt={6}>
                  <Typography variant="h5" fontWeight="bold" mb={3}>
                    Project Screenshots
                  </Typography>

                  <Grid container spacing={3}>
                    {project.screenshots.map((img, i) => (
                      <Grid key={i} size={{ xs: 12, md: 6 }}>
                        <Box
                          component="img"
                          src={img}
                          alt="screenshot"
                          sx={{
                            width: "100%",
                            borderRadius: 3,
                            transition: "0.3s",
                            cursor: "pointer",
                            "&:hover": { transform: "scale(1.02)" },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box> */}

                {/* CTA SECTION */}
                <Box
                  sx={{
                    mt: 4,
                    p: 6,
                    textAlign: "center",
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h5" fontWeight="400">
                    Interested in a similar project?
                  </Typography>

                  <Typography color="text.secondary" mt={1} mb={3}>
                    Let's discuss how we can bring your ideas to life.
                  </Typography>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: "bold",
                      background: theme.palette.custom.buttonGradient,
                    }}
                  >
                    Start a Similar Project
                  </Button>
                </Box>

              </Container>
            ) : (
              <Container maxWidth="lg">
                {/* HEADER */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
                    Back to Portfolio
                  </Button>
                  <SharePopover url={typeof window !== 'undefined' ? window.location.href : ''} title={project?.title} />
                </Box>
                {/* BODY */}
                <Typography variant="h5" color="text.secondary" textAlign="center" mt={10}>
                  Something went wrong while fetching the project details. Please try again later.
                </Typography>
              </Container>
            )
        )
      }
    </Box>
  );
};

export default ProjectDetail;
